import PropTypes from 'prop-types';

/**
 * Light or dark, on the television.
 *
 * Not the phone's theme -- that one is in the account menu and lives only in
 * this browser. This is a device setting: it is saved to the box and the
 * television picks it up on its next settings poll, within about half a minute.
 *
 * Shown as two miniatures rather than a switch. The television is in another
 * room from whoever is choosing, often another building, so the one thing the
 * control has to do is show what the answer looks like. The miniatures are the
 * real palette from the display's stylesheet, drawn at the same proportions:
 * the scrim, the arch, the gold line as far as the sun has travelled, and a
 * card. Nothing here loads an image -- it is two inline SVGs.
 */

const THEMES = [
  {
    id:    'dark',
    label: 'Dark',
    note:  'White type over a shaded picture.',
    ink:   '#ffffff',
    scrim: '#0a2530',
    panel: 'rgba(6, 22, 30, .72)',
    hair:  'rgba(255, 255, 255, .28)',
    rule:  'rgba(255, 255, 255, .3)',
    gold:  '#D9A93A',
    sky:   ['#123c4d', '#2c5f5a'],
  },
  {
    id:    'light',
    label: 'Light',
    note:  'Dark type over a brightened picture.',
    ink:   '#1B1205',
    scrim: '#f6f0e4',
    panel: 'rgba(255, 252, 246, .86)',
    hair:  'rgba(27, 18, 5, .26)',
    rule:  'rgba(27, 18, 5, .26)',
    gold:  '#8A6A12',
    sky:   ['#d8cbb0', '#efe4cd'],
  },
];

const Preview = ({ t }) => (
  <svg className="tvmini" viewBox="0 0 160 90" aria-hidden="true">
    <defs>
      <linearGradient id={`sky-${t.id}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"  stopColor={t.sky[0]} />
        <stop offset="100%" stopColor={t.sky[1]} />
      </linearGradient>
      <linearGradient id={`veil-${t.id}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor={t.scrim} stopOpacity=".9" />
        <stop offset="45%"  stopColor={t.scrim} stopOpacity="0" />
        <stop offset="70%"  stopColor={t.scrim} stopOpacity=".55" />
        <stop offset="100%" stopColor={t.scrim} stopOpacity=".95" />
      </linearGradient>
    </defs>

    {/* the customer's picture, standing in for itself */}
    <rect width="160" height="90" fill={`url(#sky-${t.id})`} />
    <rect width="160" height="90" fill={`url(#veil-${t.id})`} />

    {/* the clock and its date line */}
    <rect x="10" y="9"  width="34" height="9"   rx="1.5" fill={t.ink} opacity=".92" />
    <rect x="10" y="21" width="24" height="3.2" rx="1.6" fill={t.ink} opacity=".5" />

    {/* the arch, and the gold as far as the day has gone */}
    <path d="M 24 66 Q 80 38 136 66" fill="none" stroke={t.rule} strokeWidth="2" strokeLinecap="round" />
    <path d="M 24 66 Q 52 52 80 47"  fill="none" stroke={t.gold} strokeWidth="2.6" strokeLinecap="round" />

    {/* two of the six discs, and the one being waited for */}
    <circle cx="24" cy="66" r="5"   fill={t.panel} stroke={t.gold} strokeWidth="1" />
    <circle cx="80" cy="47" r="6.5" fill={t.panel} stroke={t.gold} strokeWidth="1.4" />
    <circle cx="136" cy="66" r="5"  fill={t.panel} stroke={t.hair} strokeWidth="1" />

    {/* a card under the arch */}
    <rect x="58" y="62" width="44" height="21" rx="4" fill={t.panel} stroke={t.hair} strokeWidth="1" />
    <rect x="70" y="66" width="20" height="3"   rx="1.5" fill={t.ink} opacity=".6" />
    <rect x="66" y="71" width="28" height="8"   rx="1.5" fill={t.gold} />
  </svg>
);

Preview.propTypes = { t: PropTypes.object.isRequired };

const SettingDisplayTheme = ({ DeviceSetting, onSettingChange }) => {
  // Anything that is not the word "light" is dark -- which includes the missing
  // value a box enrolled before this setting existed reports.
  const current = DeviceSetting['display-theme'] === 'light' ? 'light' : 'dark';

  const choose = (id) => {
    if (id === current) return;
    onSettingChange([{ ...DeviceSetting, 'display-theme': id }]);
  };

  return (
    <div className="tvtheme">
      {THEMES.map((t) => (
        <button
          type="button"
          key={t.id}
          className={'tvtheme__pick' + (current === t.id ? ' tvtheme__pick--on' : '')}
          aria-pressed={current === t.id}
          onClick={() => choose(t.id)}
        >
          <Preview t={t} />
          <span className="tvtheme__name">{t.label}</span>
          <small>{t.note}</small>
        </button>
      ))}
    </div>
  );
};

SettingDisplayTheme.propTypes = {
  DeviceSetting:   PropTypes.object.isRequired,
  onSettingChange: PropTypes.func.isRequired,
};

export default SettingDisplayTheme;
